import React, { useEffect, useState, useRef, useCallback } from "react";
import {
  Trash,
  X,
  MessageCirclePlus,
  Search,
  Compass,
  Tag,
  RefreshCw,
  ChevronDown,
  ChevronRight,
  Plus,
} from "lucide-react";
import { useNavigate, Link } from "react-router-dom";

import { useUserSessions } from "../../api/useUserSessions";

const SectionHeader = ({ icon: Icon, title, collapsible, isCollapsed, onToggle }) => (
  <button
    onClick={collapsible ? onToggle : undefined}
    className={`flex items-center w-full px-1 pt-6 pb-2 mb-2 border-b border-neutral-200 ${
      collapsible ? 'cursor-pointer hover:bg-neutral-50 transition-colors' : ''
    }`}
    disabled={!collapsible}
  >
    {collapsible && (
      isCollapsed ? (
        <ChevronRight size={14} className="mr-1 text-penta-grey-medium" strokeWidth={1.5} />
      ) : (
        <ChevronDown size={14} className="mr-1 text-penta-grey-medium" strokeWidth={1.5} />
      )
    )}
    <Icon size={16} className="mr-2 text-penta-grey-medium" strokeWidth={1.5} />
    <h3 className="text-xs font-mono text-penta-grey-medium uppercase tracking-wider">
      {title}
    </h3>
  </button>
);

// Helper to format relative timestamps
const formatRelativeTime = (dateString) => {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now - date;
  const diffMins = Math.floor(diffMs / (1000 * 60));
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays === 1) return 'Yesterday';
  if (diffDays < 7) return `${diffDays}d ago`;
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

const ChatHistory = ({ setIsHistoryOpen, currentSessionId }) => {
  const [sessions, setSessions] = useState({});
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isHistoryCollapsed, setIsHistoryCollapsed] = useState(false);
  const dataFetchedRef = useRef(false);
  const navigate = useNavigate();
  const { deleteUserSession, getUserSessions, createUserSession } =
    useUserSessions();

  // Handle window resize
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Load sessions only once when component mounts
  const loadSessions = useCallback(async () => {
    // Prevent loading if already fetched or in progress
    if (dataFetchedRef.current || isLoading) return;

    setIsLoading(true);
    setError(null);

    try {
      const data = await getUserSessions();
      setSessions(data.sessions || {});
      dataFetchedRef.current = true; // Mark as fetched
    } catch (error) {
      console.error("Error loading sessions:", error);
      setError(error.message);
    } finally {
      setIsLoading(false);
    }
  }, [getUserSessions, isLoading]);

  // Use a separate effect to trigger the loading
  useEffect(() => {
    loadSessions();
  }, [loadSessions]);

  const handleSessionSelect = (sessionId) => {
    navigate(`/chats/${sessionId}`);
    if (isMobile && setIsHistoryOpen) {
      setIsHistoryOpen(false);
    }
  };

  const handleDeleteSession = async (e, sessionId) => {
    e.stopPropagation();
    if (!window.confirm("Are you sure you want to delete this chat?")) {
      return;
    }
    try {
      await deleteUserSession(sessionId);
      setSessions((prev) => {
        const newSessions = { ...prev };
        delete newSessions[sessionId];
        return newSessions;
      });
      if (sessionId === currentSessionId) {
        navigate("/");
      }
    } catch (error) {
      console.error("Error deleting session:", error);
      alert("Failed to delete chat. Please try again.");
    }
  };

  const handleNewSession = async () => {
    navigate(`/`);
    setIsHistoryOpen(false);
  };

  const handleNavigation = (path) => {
    navigate(path);
    if (isMobile && setIsHistoryOpen) {
      setIsHistoryOpen(false);
    }
  };

  const handleSessionKeyDown = (event, sessionId) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      handleSessionSelect(sessionId);
    }
  };

  // Function to create a new session
  const createNewSession = async () => {
    setIsLoading(true);
    try {
      const result = await createUserSession();
      console.log("Created new session:", result);

      // Refresh the session list
      dataFetchedRef.current = false;
      await loadSessions();

      // Navigate to the new session
      if (result && result.sessionId) {
        navigate(`/chats/${result.sessionId}`);
      }
    } catch (error) {
      console.error("Error creating session:", error);
      setError(error.message);
    } finally {
      setIsLoading(false);
    }
  };

  // Display error if sessions couldn't be loaded
  if (error) {
    return (
      <div className="flex flex-col h-full font-sans font-light bg-white">
        <div className="flex justify-between items-center p-4 border-b border-neutral-200">
          <button
            onClick={() => setIsHistoryOpen(false)}
            className="p-2 hover:opacity-70 transition-opacity duration-200"
            aria-label="Close panel"
          >
            <X size={20} strokeWidth={1.5} className="text-penta-black" />
          </button>
          <div className="flex">
            <button
              onClick={createNewSession}
              className="p-2 hover:opacity-70 transition-opacity duration-200"
              aria-label="Create a new session"
              disabled={isLoading}
            >
              <RefreshCw
                size={20}
                strokeWidth={1.5}
                className={`text-penta-black ${isLoading ? "animate-spin" : ""}`}
              />
            </button>
            <button
              onClick={handleNewSession}
              className="p-2 hover:opacity-70 transition-opacity duration-200"
              aria-label="New chat"
            >
              <MessageCirclePlus
                size={20}
                strokeWidth={1.5}
                className="text-penta-black"
              />
            </button>
          </div>
        </div>
        <div className="p-4 text-penta-coral">
          Error loading sessions: {error}
          <button
            onClick={() => {
              dataFetchedRef.current = false;
              loadSessions();
            }}
            className="mt-2 px-4 py-2 bg-penta-mint text-penta-black rounded-full text-sm font-mono uppercase tracking-wide hover:opacity-90 transition-opacity duration-200"
          >
            Retry
          </button>
          <button
            onClick={createNewSession}
            className="mt-2 ml-2 px-4 py-2 bg-penta-black text-white rounded-full text-sm font-mono uppercase tracking-wide hover:opacity-90 transition-opacity duration-200"
            disabled={isLoading}
          >
            Create New Session
          </button>
        </div>
      </div>
    );
  }

  const groupSessionsByDate = () => {
    if (!Object.entries(sessions).length) return {};
    const sortedSessions = Object.entries(sessions).sort(
      ([, a], [, b]) => new Date(b.createdAt) - new Date(a.createdAt)
    );
    const grouped = {};
    sortedSessions.forEach(([sessionId, session]) => {
      const date = new Date(session.createdAt);
      const dateString = date.toLocaleDateString("en-US", {
        month: "long",
        day: "numeric",
        year: "numeric",
      });
      if (!grouped[dateString]) {
        grouped[dateString] = [];
      }
      grouped[dateString].push([sessionId, session]);
    });
    return grouped;
  };

  const groupedSessions = groupSessionsByDate();

  return (
    <div className="flex flex-col h-full font-sans font-light bg-white">
      <div className="flex justify-between items-center p-4 border-b border-neutral-200">
        <button
          onClick={() => setIsHistoryOpen(false)}
          className="p-2 hover:opacity-70 transition-opacity duration-200"
          aria-label="Close panel"
        >
          <X size={20} strokeWidth={1.5} className="text-penta-black" />
        </button>
        <div className="flex space-x-1">
          <button
            onClick={createNewSession}
            className="p-2 hover:opacity-70 transition-opacity duration-200"
            aria-label="Create a new session"
            disabled={isLoading}
          >
            <RefreshCw
              size={20}
              strokeWidth={1.5}
              className={`text-penta-black ${isLoading ? "animate-spin" : ""}`}
            />
          </button>
          <button
            onClick={handleNewSession}
            className="p-2 hover:opacity-70 transition-opacity duration-200"
            aria-label="New chat"
          >
            <MessageCirclePlus
              size={20}
              strokeWidth={1.5}
              className="text-penta-black"
            />
          </button>
        </div>
      </div>

      <div className="overflow-y-auto flex-1 p-4 space-y-4">
        {/* Prominent New Conversation Button */}
        <button
          onClick={handleNewSession}
          className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-penta-mint text-white rounded-lg font-medium hover:bg-penta-mint/90 transition-colors"
        >
          <Plus size={18} />
          New Conversation
        </button>

        {isLoading ? (
          <div className="text-center py-4">Loading sessions...</div>
        ) : (
          <>
            <section>
              <SectionHeader
                icon={Search}
                title="Recent Conversations"
                collapsible
                isCollapsed={isHistoryCollapsed}
                onToggle={() => setIsHistoryCollapsed(!isHistoryCollapsed)}
              />
              {!isHistoryCollapsed && (
                <>
                  {Object.entries(sessions).length === 0 && (
                    <div className="text-center text-sm text-penta-grey-medium py-4">
                      No recent conversations
                    </div>
                  )}
                  {Object.entries(groupedSessions).map(
                    ([dateString, sessionsGroup]) => (
                      <div key={dateString} className="mb-4">
                        <h4 className="text-xs text-penta-grey-medium font-mono mb-1 px-1">
                          {dateString}
                        </h4>
                        {sessionsGroup.map(([sessionId, session]) => (
                          <div
                            key={sessionId}
                            role="button"
                            tabIndex={0}
                            onClick={() => handleSessionSelect(sessionId)}
                            onKeyDown={(e) => handleSessionKeyDown(e, sessionId)}
                            className={`w-full px-3 py-2.5 my-1 text-sm flex group justify-between items-start cursor-pointer transition-all duration-200 text-left rounded ${
                              sessionId === currentSessionId
                                ? "bg-penta-grey-light text-penta-black border-l-2 border-penta-mint"
                                : "hover:bg-penta-grey-light focus:outline-none"
                            }`}
                          >
                            <div className="flex-1 min-w-0 pr-2">
                              <div className="flex items-center justify-between gap-2">
                                <span className="font-medium truncate">
                                  {session.name || "New Chat"}
                                </span>
                                <span className="text-[10px] text-penta-grey-medium flex-shrink-0">
                                  {formatRelativeTime(session.createdAt)}
                                </span>
                              </div>
                              {session.preview && (
                                <p className="text-xs text-penta-grey-medium truncate mt-0.5">
                                  {session.preview}
                                </p>
                              )}
                            </div>
                            <button
                              type="button"
                              onClick={(e) => handleDeleteSession(e, sessionId)}
                              className="p-1 opacity-0 group-hover:opacity-100 hover:text-penta-coral transition-opacity duration-150 focus:opacity-100 focus:outline-none flex-shrink-0"
                              aria-label="Delete chat"
                            >
                              <Trash size={14} />
                            </button>
                          </div>
                        ))}
                      </div>
                    )
                  )}
                </>
              )}
            </section>

            <section>
              <SectionHeader icon={Compass} title="Browse" />
              <div className="flex flex-col space-y-1">
                <Link
                  to="/p2p/browse"
                  onClick={() => handleNavigation("/p2p/browse")}
                  className="px-3 py-2 text-sm hover:bg-penta-grey-light transition-all duration-200 block"
                >
                  Browse Items
                </Link>
              </div>
            </section>

            <section>
              <SectionHeader icon={Tag} title="Sell" />
              <div className="flex flex-col space-y-1">
                <Link
                  to="/sell"
                  onClick={() => handleNavigation("/sell")}
                  className="px-3 py-2 text-sm hover:bg-penta-grey-light transition-all duration-200 block"
                >
                  Sell an Item
                </Link>
                <Link
                  to="/p2p/dashboard"
                  onClick={() => handleNavigation("/p2p/dashboard")}
                  className="px-3 py-2 text-sm hover:bg-penta-grey-light transition-all duration-200 block"
                >
                  My Listings
                </Link>
                <Link
                  to="/p2p/offers"
                  onClick={() => handleNavigation("/p2p/offers")}
                  className="px-3 py-2 text-sm hover:bg-penta-grey-light transition-all duration-200 block"
                >
                  Offers
                </Link>
                <Link
                  to="/settings"
                  onClick={() => handleNavigation("/settings")}
                  className="px-3 py-2 text-sm hover:bg-penta-grey-light transition-all duration-200 block"
                >
                  Seller Settings
                </Link>
              </div>
            </section>
          </>
        )}
      </div>
    </div>
  );
};

export default ChatHistory;
